import { useGenerateTranslationVoiceovers } from '@/hooks/useGenerateTranslationVoiceovers';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';

interface GenerateTranslationVoiceoversDialogProps {
  attractionId: string;
  translationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VOICE_OPTIONS = [
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo (Masculine)' },
  { value: 'fable', label: 'Fable (British)' },
  { value: 'onyx', label: 'Onyx (Deep)' },
  { value: 'nova', label: 'Nova (Feminine)' },
  { value: 'shimmer', label: 'Shimmer (Bright)' },
] as const;

export function GenerateTranslationVoiceoversDialog({
  attractionId,
  translationId,
  open,
  onOpenChange,
}: GenerateTranslationVoiceoversDialogProps) {
  const [voiceoverType, setVoiceoverType] = useState<'both' | 'description' | 'long_description'>(
    'both',
  );
  const [voice, setVoice] = useState<'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'>(
    'alloy',
  );
  const generateVoiceoversMutation = useGenerateTranslationVoiceovers();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    generateVoiceoversMutation.mutate(
      { attractionId, translationId, voiceoverType, voice },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AudioLines className="h-5 w-5" />
              Generate Voiceovers
            </DialogTitle>
            <DialogDescription>
              Generate voiceovers for this translation using AI text-to-speech technology.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <label htmlFor="voiceoverType" className="block text-sm font-medium mb-2">
                Voiceover Type <span className="text-destructive">*</span>
              </label>
              <select
                id="voiceoverType"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voiceoverType}
                onChange={(e) =>
                  setVoiceoverType(e.target.value as 'both' | 'description' | 'long_description')
                }
                disabled={generateVoiceoversMutation.isPending}
              >
                <option value="both">Both (Short + Long)</option>
                <option value="description">Short Description Only</option>
                <option value="long_description">Long Description Only</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Choose which voiceovers to generate
              </p>
            </div>

            <div>
              <label htmlFor="voice" className="block text-sm font-medium mb-2">
                AI Voice <span className="text-destructive">*</span>
              </label>
              <select
                id="voice"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voice}
                onChange={(e) =>
                  setVoice(
                    e.target.value as 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer',
                  )
                }
                disabled={generateVoiceoversMutation.isPending}
              >
                {VOICE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Select the AI voice for text-to-speech conversion
              </p>
            </div>
          </div>

          {generateVoiceoversMutation.isPending && (
            <div className="py-2 text-sm text-muted-foreground">
              🎙️ Generating voiceovers... This may take 30-60 seconds.
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={generateVoiceoversMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={generateVoiceoversMutation.isPending}>
              Generate Voiceovers
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Export the icon for use in the custom action
export const GenerateTranslationVoiceoversIcon = AudioLines;
